/**
 * Presentation List Component
 * 
 * Implements FRDB01-03 requirements:
 * - FRDB01: Presentation list in tabular view with scores and status
 * - FRDB02: Faceted filters (date range, presenter, company, status)
 * - FRDB03: Progress indicators for active evaluations
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Alert,
    CircularProgress,
    Paper,
    Avatar,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    Tooltip,
    LinearProgress,
    Card,
    Grid,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Skeleton,
    SelectChangeEvent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import {
    VideoCall,
    Add,
    Visibility,
    Assessment,
    FilterList,
    ExpandMore,
    PlayArrow,
    Schedule,
    Error as ErrorIcon,
    Search,
    Clear,
    RestartAlt
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useUser } from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import {
    getPipelineStatusConstants,
    getStatusLabel,
    getStatusColor,
    calculateProgressPercentage,
    isPipelineInProgress,
    canRestartPipeline,
    restartFailedEvaluation
} from '../../utils/api/evaluationPipelineApi';
import { formatScore, getScoreColor, getScoreGrade } from '../../utils/api/reportingApi';
import { getPresentations } from '../../utils/api/presentationApi';
import { getConsultants } from '../../utils/api/consultantApi';
// @ts-ignore
import PresentationWizard from './wizard/PresentationWizard';

interface Presentation {
    id: string;
    description: string;
    consultantId: string;
    userId: string;
    presenterName?: string;
    productName?: string;
    language?: string;
    presentationType?: string;
    evaluationStatus?: string;
    status?: string;
    overallScore?: number;
    structureScore?: number;
    softSkillsScore?: number;
    knowledgeScore?: number;
    categoryScores?: Record<string, number>;
    finalReport?: {
        category_scores?: Record<string, number>;
    };
    createdAt: any; // Timestamp or Date
    fileSize?: number;
}

interface Consultant {
    id: string;
    firstName: string;
    lastName: string;
    company?: string;
    email?: string;
}

interface Filters {
    search: string;
    status: string;
    consultantId: string;
    dateFrom: Date | null;
    dateTo: Date | null;
    language: string;
    productName: string;
}

const StatusProgress = ({ status }: { status: string }) => {
    const inProgress = isPipelineInProgress(status);
    const progress = calculateProgressPercentage(status);
    const colorValue = getStatusColor(status);
    const color = (['primary', 'secondary', 'error', 'info', 'success', 'warning', 'default'].includes(colorValue)
        ? colorValue
        : 'default') as "primary" | "secondary" | "error" | "info" | "success" | "warning" | "default";

    const isComplete = status === 'COMPLETE' || status === 'Completed' || status === 'complete' || status === 'COMPLETE_V2';

    if (!inProgress && !isComplete) {
        return (
            <Chip
                label={getStatusLabel(status)}
                color={color === 'default' ? undefined : color}
                size="small"
                icon={status === 'FAILED' ? <ErrorIcon /> : <Schedule />}
            />
        );
    }

    return (
        <Box sx={{ minWidth: 120 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="caption" sx={{ mr: 1 }}>
                    {getStatusLabel(status)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                    {progress}%
                </Typography>
            </Box>
            <LinearProgress
                variant="determinate"
                value={progress}
                color={color === 'default' ? 'primary' : (color as any)}
            />
        </Box>
    );
};

const ScoreDisplay = ({ score, category = 'Overall' }: { score?: number, category?: string }) => {
    if (typeof score !== 'number') return <Typography variant="body2">-</Typography>;

    const color = getScoreColor(score);
    const grade = getScoreGrade(score);

    return (
        <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" fontWeight="bold" color={color}>
                {formatScore(score)}
            </Typography>
            <Chip
                label={grade}
                size="small"
                color={color as any}
                sx={{ height: 16, fontSize: '0.7rem' }}
            />
        </Box>
    );
};

// Dynamic category scores display for presentations with custom evaluation schemas
const CategoryScoresCell = ({ presentation }: { presentation: Presentation }) => {
    // Get dynamic scores or fall back to legacy fields
    let categoryScores = presentation.categoryScores ||
        presentation.finalReport?.category_scores;

    // Backwards compatibility: build from legacy fields if no dynamic scores
    if (!categoryScores || Object.keys(categoryScores).length === 0) {
        categoryScores = {};
        if (presentation.structureScore != null) categoryScores['structure'] = presentation.structureScore;
        if (presentation.softSkillsScore != null) categoryScores['soft_skills'] = presentation.softSkillsScore;
        if (presentation.knowledgeScore != null) categoryScores['knowledge'] = presentation.knowledgeScore;
    }

    if (!categoryScores || Object.keys(categoryScores).length === 0) {
        return <Typography variant="body2" color="text.secondary">-</Typography>;
    }

    return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="center">
            {Object.entries(categoryScores).map(([key, score]) => {
                // Create abbreviated label from category key (e.g., "value_proposition" -> "VP")
                const label = key.split('_').map(w => w.charAt(0).toUpperCase()).join('');
                // Create full display name for tooltip (e.g., "value_proposition" -> "Value Proposition")
                const fullName = key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                const scoreValue = typeof score === 'number' ? score.toFixed(1) : '-';

                return (
                    <Tooltip key={key} title={`${fullName}: ${scoreValue}`}>
                        <Chip
                            label={`${label}: ${scoreValue}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                    </Tooltip>
                );
            })}
        </Stack>
    );
};

const PresentationFilters = ({ filters, onFiltersChange, consultants = [], loading = false }: {
    filters: Filters,
    onFiltersChange: (filters: Filters) => void,
    consultants: Consultant[],
    loading: boolean
}) => {
    const [expanded, setExpanded] = useState(false);
    const statusOptions = getPipelineStatusConstants();

    const handleFilterChange = (field: keyof Filters, value: any) => {
        onFiltersChange({ ...filters, [field]: value });
    };

    const clearFilters = () => {
        onFiltersChange({
            search: '',
            status: '',
            consultantId: '',
            dateFrom: null,
            dateTo: null,
            language: '',
            productName: ''
        });
    };

    const hasActiveFilters = Object.values(filters).some(value =>
        value !== '' && value !== null && value !== undefined
    );

    return (
        <Card sx={{ mb: 3 }}>
            <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <FilterList />
                        <Typography variant="h6">
                            Filters {hasActiveFilters && `(${Object.values(filters).filter(Boolean).length} active)`}
                        </Typography>
                        {hasActiveFilters && (
                            <Button
                                size="small"
                                startIcon={<Clear />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    clearFilters();
                                }}
                            >
                                Clear All
                            </Button>
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 3 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Search"
                                placeholder="Search presentations..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                InputProps={{
                                    startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />
                                }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 3 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value as string)}
                                    label="Status"
                                >
                                    <MenuItem value="">All Statuses</MenuItem>
                                    {Object.entries(statusOptions).map(([key, value]) => (
                                        <MenuItem key={key} value={value as string}>
                                            {getStatusLabel(value as string)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12, md: 3 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Consultant</InputLabel>
                                <Select
                                    value={filters.consultantId}
                                    onChange={(e) => handleFilterChange('consultantId', e.target.value)}
                                    label="Consultant"
                                    disabled={loading}
                                >
                                    <MenuItem value="">All Consultants</MenuItem>
                                    {consultants.map((consultant) => (
                                        <MenuItem key={consultant.id} value={consultant.id}>
                                            {consultant.firstName} {consultant.lastName} {consultant.company && `(${consultant.company})`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{ xs: 12, md: 3 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Product Name"
                                value={filters.productName}
                                onChange={(e) => handleFilterChange('productName', e.target.value)}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 3 }}>
                            <DatePicker
                                label="Date From"
                                value={filters.dateFrom}
                                onChange={(date) => handleFilterChange('dateFrom', date)}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 3 }}>
                            <DatePicker
                                label="Date To"
                                value={filters.dateTo}
                                onChange={(date) => handleFilterChange('dateTo', date)}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </Grid>

                        <Grid size={{ xs: 12, md: 3 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Language</InputLabel>
                                <Select
                                    value={filters.language}
                                    onChange={(e) => handleFilterChange('language', e.target.value)}
                                    label="Language"
                                >
                                    <MenuItem value="">All Languages</MenuItem>
                                    <MenuItem value="en">English</MenuItem>
                                    <MenuItem value="es">Spanish</MenuItem>
                                    <MenuItem value="pt">Portuguese</MenuItem>
                                    <MenuItem value="fr">French</MenuItem>
                                    <MenuItem value="de">German</MenuItem>
                                    <MenuItem value="auto">Auto-detected</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Card>
    );
};

const PresentationList: React.FC = () => {
    const { user, userProfile, isTenantAdmin, isGlobalAdmin } = useUser();
    useDocumentTitle('Presentations');
    const navigate = useNavigate();

    const [presentations, setPresentations] = useState<Presentation[]>([]);
    const [consultants, setConsultants] = useState<Consultant[]>([]);
    const [consultantLookup, setConsultantLookup] = useState<Record<string, any>>({});
    const [consultantsLoaded, setConsultantsLoaded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedPresentation, setSelectedPresentation] = useState<Presentation | null>(null);
    const [restartDialog, setRestartDialog] = useState(false);
    const [restarting, setRestarting] = useState(false);
    const [showWizard, setShowWizard] = useState(false);

    const [filters, setFilters] = useState<Filters>({
        search: '',
        status: '',
        consultantId: '',
        dateFrom: null,
        dateTo: null,
        language: '',
        productName: ''
    });

    const loadPresentations = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            // Load presentations from Firestore using the API
            const result = await getPresentations() as { success: boolean; presentations?: any[]; error?: string };

            if (result.success) {
                setPresentations((result.presentations || []) as Presentation[]);
            } else {
                throw new Error(result.error || 'Failed to load presentations');
            }

        } catch (error: any) {
            console.error('Error loading presentations:', error);
            setError(error.message || 'Failed to load presentations');
        } finally {
            setLoading(false);
        }
    }, []); // Empty dependency array - only load once on mount or when explicitly called

    // Helper function to create lookup with current user info
    const createUserLookup = useCallback(() => {
        const lookup: Record<string, any> = {};

        if (user) {
            const userName = user.displayName ||
                (userProfile?.firstName && userProfile?.lastName
                    ? `${userProfile.firstName} ${userProfile.lastName}`
                    : user.email) || 'Current User';

            lookup[user.uid] = {
                fullName: userName,
                company: userProfile?.company || '',
                email: user.email || ''
            };
        }

        console.log('👤 Created user-only lookup:', lookup);
        setConsultants([]);
        setConsultantLookup(lookup);
        setConsultantsLoaded(true);
    }, [user, userProfile]);

    const loadConsultants = useCallback(async () => {
        console.log('🚀 Loading consultants...');
        try {
            console.log('📡 Making API call to getConsultants...');
            const result = await getConsultants({}) as { success: boolean; consultants?: any[]; error?: string };
            console.log('📊 Consultants API result:', result);

            if (result.success && Array.isArray(result.consultants)) {
                const consultantList = result.consultants as Consultant[];
                console.log('👥 Loaded consultants:', consultantList.map(c => ({ id: c.id, name: `${c.firstName} ${c.lastName}` })));
                setConsultants(consultantList);

                // Create lookup map for fast consultant resolution
                const lookup: Record<string, any> = {};
                consultantList.forEach(consultant => {
                    lookup[consultant.id] = {
                        fullName: `${consultant.firstName} ${consultant.lastName}`,
                        company: consultant.company || '',
                        email: consultant.email || ''
                    };
                });

                // Add current user to lookup as well
                if (user) {
                    const userName = user.displayName ||
                        (userProfile?.firstName && userProfile?.lastName
                            ? `${userProfile.firstName} ${userProfile.lastName}`
                            : user.email) || 'Current User';

                    lookup[user.uid] = {
                        fullName: userName,
                        company: userProfile?.company || '',
                        email: user.email || ''
                    };
                }

                console.log('🗂️ Created consultant lookup:', lookup);
                setConsultantLookup(lookup);
                setConsultantsLoaded(true);
            } else {
                console.warn('Failed to load consultants:', result);
                console.warn('Result success:', result.success, 'Result consultants:', result.consultants);
                // Still create lookup with current user info even if consultants fail to load
                createUserLookup();
                setConsultantsLoaded(true);
            }
        } catch (error: any) {
            console.error('🚨 Error loading consultants:', error);
            console.error('Error details:', {
                message: error.message,
                stack: error.stack,
                name: error.name
            });
            // Handle index errors gracefully - consultants might not be available yet
            if (error.message && error.message.includes('index')) {
                console.log('Consultant indexes still building, will show user names where available');
            }
            createUserLookup();
            setConsultantsLoaded(true);
        }
    }, [createUserLookup, user, userProfile]);

    // Helper function to get presenter information
    const getPresenterInfo = (presentation: Presentation) => {
        // Debug logging to trace the issue
        console.log('🔍 DEBUG getPresenterInfo:', {
            presentationId: presentation.id,
            consultantId: presentation.consultantId,
            userId: presentation.userId,
            consultantsLoaded,
            consultantLookupKeys: Object.keys(consultantLookup),
            presentation: {
                description: presentation.description,
                consultantId: presentation.consultantId,
                userId: presentation.userId
            }
        });

        // If consultants haven't finished loading, show loading state
        if (!consultantsLoaded) {
            console.log('📋 Consultants not loaded yet, showing loading...');
            return {
                fullName: 'Loading...',
                company: '',
                email: ''
            };
        }

        // Check if we have a consultantId and it's different from userId (indicating coach mode)
        if (presentation.consultantId &&
            presentation.consultantId !== presentation.userId &&
            consultantLookup[presentation.consultantId]) {
            console.log('✅ Found external consultant:', presentation.consultantId, consultantLookup[presentation.consultantId]);
            return consultantLookup[presentation.consultantId];
        }

        // Debug: Check if consultantId exists but not found in lookup
        if (presentation.consultantId &&
            presentation.consultantId !== presentation.userId &&
            !consultantLookup[presentation.consultantId]) {
            console.log('⚠️ External consultant not found in lookup:', {
                consultantId: presentation.consultantId,
                availableKeys: Object.keys(consultantLookup)
            });
        }

        // Check if we have a consultantId that matches userId (regular mode or self-presentation)  
        if (presentation.consultantId &&
            presentation.consultantId === presentation.userId &&
            consultantLookup[presentation.consultantId]) {
            console.log('👤 Using self-presentation:', presentation.consultantId, consultantLookup[presentation.consultantId]);
            return consultantLookup[presentation.consultantId];
        }

        // Fallback: Use userId if we have it in our lookup
        if (presentation.userId && consultantLookup[presentation.userId]) {
            console.log('🔄 Using userId fallback:', presentation.userId, consultantLookup[presentation.userId]);
            return consultantLookup[presentation.userId];
        }

        // Final fallback for unknown consultants or missing data
        console.log('❌ No presenter info found, using final fallback');
        return {
            fullName: presentation.presenterName || 'Unknown Presenter',
            company: '',
            email: ''
        };
    };

    const handleRestartEvaluation = async () => {
        if (!selectedPresentation) return;

        try {
            setRestarting(true);
            const result = await restartFailedEvaluation(selectedPresentation.id) as { success: boolean; error?: string };

            if (result.success) {
                // Reload presentations to show updated status
                await loadPresentations();
                setRestartDialog(false);
                setSelectedPresentation(null);
            } else {
                setError(result.error || 'Failed to restart evaluation');
            }
        } catch (error: any) {
            setError(error.message || 'Failed to restart evaluation');
        } finally {
            setRestarting(false);
        }
    };

    // Load data when user is available
    // Note: filters are applied client-side, so they don't need to trigger a reload
    useEffect(() => {
        if (user) {
            loadPresentations();
        }
    }, [user, loadPresentations]);

    // Load consultants when user data is available
    useEffect(() => {
        console.log('🎯 Consultant loading effect triggered:', {
            user: !!user,
            userProfile: !!userProfile,
            userId: user?.uid,
            userProfileData: userProfile
        });
        if (user) {
            console.log('🚀 Attempting to load consultants...');
            loadConsultants();
        } else {
            console.log('⚠️ User not ready yet');
        }
    }, [user, loadConsultants]);

    const handleViewPresentation = (presentation: Presentation) => {
        navigate(`/presentations/${presentation.id}`);
    };

    const handleViewReport = (presentationId: string) => {
        navigate(`/presentations/${presentationId}/report`);
    };

    const handleStartEvaluation = async (presentation: Presentation) => {
        // TODO: Implement start evaluation
        console.log('Start evaluation for:', presentation.id);
    };

    const filteredPresentations = presentations.filter(presentation => {
        if (filters.search && !presentation.description.toLowerCase().includes(filters.search.toLowerCase())) {
            return false;
        }
        // Use robust status resolution
        const currentStatus = presentation.evaluationStatus || presentation.status || 'NEW';
        if (filters.status && currentStatus !== filters.status) {
            return false;
        }
        if (filters.consultantId && presentation.consultantId !== filters.consultantId) {
            return false;
        }
        if (filters.productName && !presentation.productName?.toLowerCase().includes(filters.productName.toLowerCase())) {
            return false;
        }
        if (filters.language && presentation.language !== filters.language) {
            return false;
        }
        // TODO: Add date filtering
        return true;
    }).sort((a, b) => {
        // Sort by creation date in descending order (newest first)
        const dateA = a.createdAt ? (a.createdAt.seconds || a.createdAt) : 0;
        const dateB = b.createdAt ? (b.createdAt.seconds || b.createdAt) : 0;
        return dateB - dateA;
    });

    const paginatedPresentations = filteredPresentations.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    if (loading) {
        return (
            <Box sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Skeleton variant="text" width="40%" height={60} />
                    <Skeleton variant="rectangular" width={180} height={40} />
                </Box>
                <Card sx={{ mb: 3 }}>
                    <Skeleton variant="rectangular" height={100} />
                </Card>
                {[1, 2, 3].map(i => (
                    <Skeleton key={i} variant="rectangular" height={80} sx={{ mb: 2 }} />
                ))}
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" gutterBottom>
                        Presentations
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Upload and manage presentation videos for AI evaluation
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => setShowWizard(true)}
                >
                    New Presentation
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Filters */}
            <PresentationFilters
                filters={filters}
                onFiltersChange={setFilters}
                consultants={consultants}
                loading={loading}
            />

            {/* Results Summary */}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                    Showing {paginatedPresentations.length} of {filteredPresentations.length} presentations
                </Typography>
            </Box>

            {filteredPresentations.length === 0 ? (
                <Paper sx={{ p: 6, textAlign: 'center' }}>
                    <Avatar sx={{ bgcolor: 'grey.100', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                        <VideoCall />
                    </Avatar>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {presentations.length === 0 ? 'No presentations found' : 'No presentations match your filters'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {presentations.length === 0
                            ? 'Upload your first presentation to start the evaluation process'
                            : 'Try adjusting your search criteria or clear the filters'
                        }
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => setShowWizard(true)}
                    >
                        New Presentation
                    </Button>
                </Paper>
            ) : (
                <>
                    {/* Presentations Table */}
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Presentation</TableCell>
                                    <TableCell>Consultant</TableCell>
                                    <TableCell>Product</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Overall Score</TableCell>
                                    <TableCell align="center">Category Scores</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedPresentations.map((presentation) => (
                                    <TableRow key={presentation.id} hover>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {presentation.description}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    {presentation.language} • {presentation.presentationType}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box>
                                                <Typography variant="body2">
                                                    {getPresenterInfo(presentation).fullName}
                                                </Typography>
                                                {getPresenterInfo(presentation).company && (
                                                    <Typography variant="caption" color="textSecondary">
                                                        {getPresenterInfo(presentation).company}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{presentation.productName || '-'}</TableCell>
                                        <TableCell>
                                            <StatusProgress status={presentation.evaluationStatus || presentation.status || 'NEW'} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <ScoreDisplay score={presentation.overallScore} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <CategoryScoresCell presentation={presentation} />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {presentation.createdAt ?
                                                    new Date(presentation.createdAt.seconds * 1000).toLocaleDateString() :
                                                    '-'
                                                }
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{ minWidth: 140 }}>
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <Tooltip title="View Details">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleViewPresentation(presentation)}
                                                    >
                                                        <Visibility />
                                                    </IconButton>
                                                </Tooltip>

                                                {(['COMPLETE', 'Completed'].includes(presentation.evaluationStatus || 'NEW')) && (
                                                    <Tooltip title="View Report">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewReport(presentation.id)}
                                                            color="primary"
                                                        >
                                                            <Assessment />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {(presentation.evaluationStatus || 'NEW') === 'NEW' && (
                                                    <Tooltip title="Start Evaluation">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleStartEvaluation(presentation)}
                                                            color="success"
                                                        >
                                                            <PlayArrow />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}

                                                {canRestartPipeline(presentation.evaluationStatus || 'NEW') && (isTenantAdmin() || isGlobalAdmin()) && (
                                                    <Tooltip title="Restart Evaluation">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setSelectedPresentation(presentation);
                                                                setRestartDialog(true);
                                                            }}
                                                            color="warning"
                                                        >
                                                            <RestartAlt />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            {/* Pagination */}
                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25]}
                                component="div"
                                count={filteredPresentations.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={(e, p) => setPage(p)}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                            />
                        </Table>
                    </TableContainer>
                </>
            )}

            {/* Restart Evaluation Dialog */}
            <Dialog
                open={restartDialog}
                onClose={() => setRestartDialog(false)}
            >
                <DialogTitle>Restart Evaluation?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to restart the evaluation for "{selectedPresentation?.description}"?
                        This will re-run the entire analysis pipeline.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRestartDialog(false)} disabled={restarting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRestartEvaluation}
                        color="primary"
                        variant="contained"
                        disabled={restarting}
                        startIcon={restarting ? <CircularProgress size={20} /> : <RestartAlt />}
                    >
                        Restart Evaluation
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Presentation Wizard */}
            <PresentationWizard
                open={showWizard}
                onClose={() => setShowWizard(false)}
                onSuccess={() => {
                    setShowWizard(false);
                    loadPresentations();
                }}
            />
        </Box>
    );
};

export default PresentationList;
